<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { DgrIcon } from '@stockmarkteam/donguri-ui';

interface Props {
  currentPage: number;
  totalPages: number;
  maxVisiblePages?: number;
}

const props = withDefaults(defineProps<Props>(), {
  maxVisiblePages: 5,
});

const route = useRoute();

const visiblePageNumbers = computed(() => {
  const { currentPage, totalPages, maxVisiblePages } = props;
  const displayPageCount = Math.min(totalPages, maxVisiblePages);

  const start = Math.max(
    1,
    // 現在ページを中央配置すると存在するページ範囲を超える場合は、
    // 存在するページ範囲内に収まる最大開始位置を使用する
    Math.min(
      currentPage - Math.floor(displayPageCount / 2),
      totalPages - displayPageCount + 1,
    ),
  );

  return Array.from({ length: displayPageCount }, (_, i) => start + i);
});

const isFirstPage = computed(() => props.currentPage === 1);
const isLastPage = computed(() => props.currentPage === props.totalPages);

const getPageQuery = (page: number) => {
  return { ...route.query, page: page.toString() };
};
</script>

<template>
  <div class="pagination-navigator c-title-bold--m">
    <router-link
      :to="{ query: getPageQuery(1) }"
      class="c-btn c-btn--auto c-btn--small nav-arrow"
      :class="{ disabled: isFirstPage }"
    >
      <DgrIcon
        name="double-chevron-left"
        size="small"
        :keep-fill="false"
        class="arrow-icon"
      />
    </router-link>

    <router-link
      :to="{ query: getPageQuery(currentPage - 1) }"
      class="c-btn c-btn--auto c-btn--small nav-arrow"
      :class="{ disabled: isFirstPage }"
    >
      <DgrIcon
        name="chevron-left"
        size="small"
        :keep-fill="false"
        class="arrow-icon"
      />
    </router-link>

    <router-link
      v-for="page in visiblePageNumbers"
      :key="page"
      :to="{ query: getPageQuery(page) }"
      class="c-btn c-btn--auto c-btn--small page-button"
      :class="{ active: page === currentPage }"
    >
      {{ page }}
    </router-link>

    <router-link
      :to="{ query: getPageQuery(currentPage + 1) }"
      class="c-btn c-btn--auto c-btn--small nav-arrow"
      :class="{ disabled: isLastPage }"
    >
      <DgrIcon
        name="chevron-right"
        size="small"
        :keep-fill="false"
        class="arrow-icon"
      />
    </router-link>

    <router-link
      :to="{ query: getPageQuery(totalPages) }"
      class="c-btn c-btn--auto c-btn--small nav-arrow"
      :class="{ disabled: isLastPage }"
    >
      <DgrIcon
        name="double-chevron-right"
        size="small"
        :keep-fill="false"
        class="arrow-icon"
      />
    </router-link>
  </div>
</template>

<style scoped lang="scss">
.pagination-navigator {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  justify-content: center;
  a {
    height: 32px;
    border: none;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  a.page-button {
    width: 32px;
  }
  a.nav-arrow {
    padding: 0 8px;
    &.disabled {
      pointer-events: none;
      background: $color-gray400;
    }
  }
  a.active {
    background-color: $color-gray1000;
    color: $color-white;
  }
}
</style>
